import React from 'react';
import { sanitizeHtml } from '../utils/sanitize';

/**
 * Platform-native mockup frames.
 * Each wraps content in a styled chrome that looks like the destination platform.
 */

// ── dev.to Article Mockup ─────────────────────────────────────────────

export function DevToMock({ title, bodyHtml, tags }: { title: string; bodyHtml: string; tags?: string[] }) {
  return (
    <div className="mock mock-devto">
      <div className="mock-devto__header">
        <div className="mock-devto__author">
          <div className="mock-devto__avatar" />
          <div>
            <div className="mock-devto__name">Author</div>
            <div className="mock-devto__date">Posted today</div>
          </div>
        </div>
        <div className="mock-devto__reactions">
          <span>{'\u2764'}</span> <span>{'\u{1F4AC}'}</span> <span>{'\u{1F516}'}</span>
        </div>
      </div>
      <h1 className="mock-devto__title">{title}</h1>
      {tags && tags.length > 0 && (
        <div className="mock-devto__tags">
          {tags.map((t, i) => <span key={i} className="mock-devto__tag">#{t}</span>)}
        </div>
      )}
      <div className="mock-devto__body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }} />
    </div>
  );
}

// ── LinkedIn Post Mockup ──────────────────────────────────────────────

export function LinkedInMock({ text, imageUrl, brandName }: { text: string; imageUrl?: string; brandName?: string }) {
  return (
    <div className="mock mock-linkedin">
      <div className="mock-linkedin__header">
        <div className="mock-linkedin__avatar" />
        <div>
          <div className="mock-linkedin__name">{brandName || 'Author'}</div>
          <div className="mock-linkedin__meta">1st &middot; Just now &middot; {'\u{1F30E}'}</div>
        </div>
      </div>
      <div className="mock-linkedin__text">{text}</div>
      {imageUrl && (
        <div className="mock-linkedin__image">
          <img src={imageUrl} alt="Social card" style={{ width: '100%', display: 'block' }} />
        </div>
      )}
      <div className="mock-linkedin__actions">
        <span>{'\u{1F44D}'} Like</span>
        <span>{'\u{1F4AC}'} Comment</span>
        <span>{'\u{1F501}'} Repost</span>
        <span>{'\u{1F4E8}'} Send</span>
      </div>
    </div>
  );
}

// ── Reddit Post Mockup ────────────────────────────────────────────────

function renderBasicMarkdown(md: string): string {
  return md
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^### (.+)$/gm, '<strong style="font-size:1.1em">$1</strong>')
    .replace(/^## (.+)$/gm, '<strong style="font-size:1.2em">$1</strong>')
    .replace(/^# (.+)$/gm, '<strong style="font-size:1.3em">$1</strong>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

export function RedditMock({ title, bodyMd, subreddit }: { title: string; bodyMd: string; subreddit?: string }) {
  const bodyHtml = renderBasicMarkdown(bodyMd.slice(0, 500) + (bodyMd.length > 500 ? '...' : ''));
  return (
    <div className="mock mock-reddit">
      <div className="mock-reddit__sidebar">
        <button className="mock-reddit__vote">{'\u25B2'}</button>
        <span className="mock-reddit__score">42</span>
        <button className="mock-reddit__vote">{'\u25BC'}</button>
      </div>
      <div className="mock-reddit__main">
        <div className="mock-reddit__meta">
          <span className="mock-reddit__sub">r/{subreddit || 'technology'}</span>
          <span>&middot; Posted by u/author &middot; just now</span>
        </div>
        <h3 className="mock-reddit__title">{title}</h3>
        <div className="mock-reddit__body">
          <div className="mock-reddit__text" style={{ fontSize: 14, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }} />
        </div>
        <div className="mock-reddit__actions">
          <span>{'\u{1F4AC}'} 12 Comments</span>
          <span>Share</span>
          <span>Save</span>
        </div>
      </div>
    </div>
  );
}

// ── Site/Blog Mockup ──────────────────────────────────────────────────

export function SiteMock({ title, bodyHtml, readingTime }: { title: string; bodyHtml: string; readingTime?: string }) {
  return (
    <div className="mock mock-site">
      <div className="mock-site__nav">
        <span className="mock-site__nav-logo">{'\u25CF'} Blog</span>
        <span className="mock-site__nav-links">About &middot; Archive</span>
      </div>
      <article className="mock-site__article">
        <h1 className="mock-site__title">{title}</h1>
        {readingTime && <div className="mock-site__reading-time">{readingTime}</div>}
        <div className="mock-site__body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }} />
      </article>
    </div>
  );
}

// ── OG Card Preview ───────────────────────────────────────────────────

export function OGCardMock({ imageUrl, title, description, domain }: { imageUrl: string; title?: string; description?: string; domain?: string }) {
  return (
    <div className="mock mock-og">
      <img src={imageUrl} alt="OG Image" className="mock-og__image" />
      {(title || description) && (
        <div className="mock-og__meta">
          {domain && <div className="mock-og__domain">{domain}</div>}
          {title && <div className="mock-og__title">{title}</div>}
          {description && <div className="mock-og__desc">{description}</div>}
        </div>
      )}
    </div>
  );
}
